import { Hub } from "aws-amplify/utils";
import { QueryClient } from "@tanstack/react-query";

import { SIGNED_IN_PAYLOAD_EVENT, SIGNED_OUT_PAYLOAD_EVENT } from "../constants/auth";
import { UserData, REACT_QUERY_AUTH_REQUIRED_KEY } from "../interfaces/account.interface";
import {
  NEED_RESET_PASSWORD_MESSAGE,
  PASSWORD_MAXIMUM_LENGTH,
  PASSWORD_MINIMUM_LENGTH
} from "../constants/form";
import { AuthContext } from "../constants/auth";
import { convertIntlMobileToLocal } from "./form.utils";
import { AccountProfile } from "../machines/userAccount/accountProfile.machine";

export const getUserDataFromProfile = (profile: AccountProfile | null): UserData => {
  return {
    username: profile?.cognitoUserId || "",
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    email: profile?.email || "",
    mobile: convertIntlMobileToLocal(profile?.mobile) || "",
    postcode: profile?.postcode || ""
  };
};

export const generateRateLimitErrorMessage = (authContext: AuthContext): string => {
  switch (authContext) {
    case AuthContext.SIGN_IN:
      return "Too many sign in attempts. Please wait a moment and try again.";
    case AuthContext.RESEND_CONFIRM_SIGN_UP_CODE:
    case AuthContext.RESEND_RESET_PASSWORD_CODE:
    case AuthContext.RESEND_EMAIL_CHANGE_CODE:
    case AuthContext.CONFIRM_EMAIL_CHANGE:
      return "You've requested too many codes. Please wait a few minutes before trying again.";
    case AuthContext.CHANGE_PASSWORD:
      return "Too many password change attempts. Please wait a few minutes before trying again.";
    default:
      return "Too many attempts. Please wait a moment and try again.";
  }
};

export const generateInvalidParameterErrorMessage = (context: AuthContext): string => {
  switch (context) {
    case AuthContext.CONFIRM_SIGN_UP:
    case AuthContext.RESEND_CONFIRM_SIGN_UP_CODE:
      return "Please enter a valid code.";
    case AuthContext.RESET_PASSWORD:
      return "Please enter a valid code and password.";
    case AuthContext.CONFIRM_SIGN_IN:
      return "Please enter a valid password.";
    case AuthContext.FORGOT_PASSWORD:
    case AuthContext.RESEND_RESET_PASSWORD_CODE:
      return "We can't reset this account automatically. Please contact support.";
    case AuthContext.EMAIL_CHANGE:
      return "Email address is invalid. Please check it and try again.";
    case AuthContext.CHANGE_PASSWORD:
      return "Your new password is not valid. Please check it and try again.";
    default:
      return "Please check your details and try again.";
  }
};

export const generateNotAuthorizedMessage = (context: AuthContext): string => {
  switch (context) {
    case AuthContext.SIGN_IN:
      return "Incorrect username or password.";
    case AuthContext.CONFIRM_SIGN_UP:
      return "This account is already verified. Please sign in.";
    case AuthContext.CONFIRM_SIGN_IN:
      return "Incorrect password. Please try again.";
    case AuthContext.EMAIL_CHANGE:
    case AuthContext.RESEND_EMAIL_CHANGE_CODE:
    case AuthContext.CONFIRM_EMAIL_CHANGE:
      return "Your session has expired. Please sign in again.";
    case AuthContext.CHANGE_PASSWORD:
      return "Your current password is incorrect.";
    default:
      return "Something went wrong. Please try again.";
  }
};

export const handleCognitoAuthError = (
  errorName: string,
  authContext: AuthContext,
  handleError: (message: string) => void,
  handleUnexpectedError: () => void,
  sideEffectHandleMethods?: {
    handleUserNotConfirmedException?: () => void;
    handlePasswordResetRequiredException?: () => void;
    handleRateLimitedException?: () => void;
    handleNotAuthorizedException?: () => void;
  }
) => {
  switch (errorName) {
    case "AliasExistsException":
      handleError("That email address is already in use by another account.");
      break;
    case "UsernameExistsException":
      handleError("An account with this email already exists. Try signing in instead.");
      break;
    case "CodeMismatchException":
      handleError(
        "That code didn't match. Please check your email for the latest code and try again."
      );
      break;
    case "ExpiredCodeException":
      handleError("That code has expired. Please request a new one.");
      break;
    case "InvalidPasswordException":
      handleError(
        `Please enter a valid password between ${PASSWORD_MINIMUM_LENGTH} and ${PASSWORD_MAXIMUM_LENGTH} characters.`
      );
      break;
    case "InvalidParameterException":
      handleError(generateInvalidParameterErrorMessage(authContext));
      break;
    case "CodeDeliveryFailureException":
      handleError(
        "We couldn't send a verification code. Please try again later or contact support."
      );
      break;
    case "NotAuthorizedException":
      sideEffectHandleMethods?.handleNotAuthorizedException();
      handleError(generateNotAuthorizedMessage(authContext));
      break;
    case "UserNotFoundException":
      handleError("Incorrect username or password.");
      break;
    case "UserNotConfirmedException":
      sideEffectHandleMethods?.handleUserNotConfirmedException();
      handleError("Your account hasn't been verified yet.");
      break;
    case "PasswordResetRequiredException":
      sideEffectHandleMethods?.handlePasswordResetRequiredException();
      handleError(NEED_RESET_PASSWORD_MESSAGE);
      break;
    case "TooManyRequestsException":
    case "LimitExceededException":
      sideEffectHandleMethods?.handleRateLimitedException();

      if (
        authContext !== AuthContext.RESEND_RESET_PASSWORD_CODE &&
        authContext !== AuthContext.RESEND_CONFIRM_SIGN_UP_CODE
      ) {
        handleError(generateRateLimitErrorMessage(authContext));
      }

      break;

    default:
      handleUnexpectedError();
  }
};

export const registerAuthQueryInvalidator = (queryClient: QueryClient) => {
  if (typeof window === "undefined") return;

  Hub.listen("auth", ({ payload }) => {
    if (payload.event === SIGNED_IN_PAYLOAD_EVENT) {
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_AUTH_REQUIRED_KEY]
      });
    }

    if (payload.event === SIGNED_OUT_PAYLOAD_EVENT) {
      queryClient.removeQueries({ queryKey: [REACT_QUERY_AUTH_REQUIRED_KEY] });
    }
  });
};
